import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { ShuttleContents } from "../../../pages/campus/shuttle";
import { RootState } from "../../../store/rootReducer";
import { Table, TableColumnsType } from 'antd';
import Button from '../../../components/Button';
import useAxios from "../../../hooks/useAxios";
import config from '../../../lib/config';
import Pagination from '../../../components/Pagination';
import Loader from "../../Loader";
import ErrorResponse from "../../ErrorResponse";
import EditLineModal from "./modals/EditLineModal";
import { deleteLine, getLines } from "../../../api/shuttleSetting.api";
import { IGetLines, ILine } from "../../../models/shuttleSetting.model";
import { useHistory } from 'react-router-dom';
import SelectCampus from "../../SelectCampus";
import useAuth from "../../../hooks/useAuth";
import ConfirmModal from "../../modals/ConfirmModal";
import useNotification from "../../../hooks/useNotification";

interface IProps {
  line: ILine | undefined;
  campusDeactived: boolean;
  setLine: (line: ILine|undefined) => void;
  setLineCode: (lineCode: number) => void;
}

const LineList: FC<IProps> = ({
  line,
  setLine,
  setLineCode,
  campusDeactived,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { successNotification } = useNotification();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetLines>();
  const { data: lineDeleteRes, isLoading: isLoading2, error: error2, execute: lineDeleteExecute } = useAxios();
  const [page, setPage] = useState(1);
  const [createLineVisible, setCreateLineVisible] = useState(false);
  const [confirmDeleteLineVisible, setConfirmDeleteLineVisible] = useState(false);

  useEffect(() => {
    doGetLines();
  }, [currentCampus]);

  useEffect(() => {
    if(lineDeleteRes && isLoading2) {
      successNotification(t('common.message.delete'));
      doGetLines();
      setConfirmDeleteLineVisible(false);
    }
  }, [lineDeleteRes, isLoading2]);

  const doGetLines = () => execute(getLines(currentCampus));

  const onDeleteLine = () => {
    if(line) {
      lineDeleteExecute(deleteLine(currentCampus, {
        lineCode: line.lineCode,
        lineName: line.lineName
      }));
    }
  }

  const columns: TableColumnsType<ILine> = [
    {
      title: t('campus.shuttle.column15'),
      dataIndex: 'lineName',
      key: 'lineName',
      align: 'center',
      sorter: (a, b) => a.lineName?.localeCompare(b.lineName),
    },
    {
      title: t('campus.shuttle.column16'),
      dataIndex: 'lineCode',
      key: 'lineCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic mint"
            onClickCapture={() => {
              setLineCode(value);
              history.push(`/campus/shuttle?index=${ShuttleContents.LineEdit}`);
            }}
            text={t('campus.shuttle.column16')}
          />
        </div>
      )
    },
    {
      title: t('campus.shuttle.column17'),
      dataIndex: 'lineCode',
      key: 'lineCode',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic mint"
            onClickCapture={() => {
              setLine(record);
              history.push(`/campus/shuttle?index=${ShuttleContents.ScheduleList}`);
            }}
            text={t('campus.shuttle.column17')}
          />
        </div>
      )
    },
    {
      title: t('common.button.delete'),
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic gray"
            onClickCapture={(e) => {
              setLine(record);
              setConfirmDeleteLineVisible(true);
              e.stopPropagation();
            }}
            text={t('common.button.delete')}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <SelectCampus className="w-150p" />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={() => {
              setLine(undefined);
              setCreateLineVisible(true);
            }}
            text={t('campus.shuttle.button1')}
            disabled={campusDeactived}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={res?.data}
        pagination={{
          current: page,
          pageSize: config.pageSize,
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              setLine(record);
              setCreateLineVisible(true);
            },
          };
        }}
        rowClassName="pointer"
        rowKey="lineCode"
      />

      <div className="comm-bx__between">
        <div></div>
        <div className="common-paging center">
          <Pagination
            count={res?.data.length || 0}
            page={page}
            setPage={setPage}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={() => {
              setLine(undefined);
              setCreateLineVisible(true);
            }}
            text={t('campus.shuttle.button1')}
            disabled={campusDeactived}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {createLineVisible &&
        <EditLineModal
          visible={createLineVisible}
          setVisible={setCreateLineVisible}
          doGetLines={doGetLines}
          line={line}
          setLine={setLine}
        />
      }

      {confirmDeleteLineVisible && (
        <ConfirmModal
          visible={confirmDeleteLineVisible}
          setVisible={setConfirmDeleteLineVisible}
          onConfirm={onDeleteLine}
          titleText={t('student.shuttle.title2')}
          messageText={t('system.menu.text3') + '\n' + t('student.shuttle.message4') + '\n' + t('student.shuttle.message5') + line?.lineName}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
          />
        )}
    </div>
  );
}

export default LineList;
